import { t } from '@lingui/core/macro'

import type { CmmsDocumentNode } from './cmmsDocument'
import { cmmsDocumentLabel } from './cmmsDocumentLabels'
import { formatCmmsMoney } from './cmmsMoney'
import type { WorkOrderEntry } from './workOrder'
import { workOrderDate } from './workOrderDates'
import type { WorkOrderPdf } from './workOrderPdf'
import { WorkOrderSheet } from './workOrderSheet'

function displayValue(node: CmmsDocumentNode): string {
  if (node.kind === 'money')
    return formatCmmsMoney(node.value, node.currency ?? undefined)
  if (node.kind === 'date') return workOrderDate(node.value)
  if (node.kind === 'quantity' && node.unit)
    return `${node.value} ${node.unit === 'hours' ? t`hours` : node.unit}`
  return node.value
}

export async function buildCmmsRecordPdf(
  entry: WorkOrderEntry
): Promise<WorkOrderPdf> {
  const document = entry.data.document
  if (!document) throw new Error('CMMS document is missing')
  const { jsPDF } = await import('jspdf')
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', compress: true })
  const sheet = new WorkOrderSheet(pdf, document.number, true)
  const allNodes = document.sections.flatMap((section) => section.nodes)
  const byId = new Map(allNodes.map((node) => [node.id, node]))
  const titleNode = byId.get(document.header.title_node_id ?? '')
  const numberNode = byId.get(document.header.number_node_id ?? '')
  sheet.header(
    cmmsDocumentLabel(document.kind),
    document.title || document.number,
    entry.data.source,
    {
      title: titleNode?.id ?? 'record',
      number: numberNode?.id ?? 'record',
    }
  )
  const status = byId.get(document.header.status_node_id ?? '')
  const statusLabel = byId.get(document.header.status_label_node_id ?? '')
  if (status)
    sheet.status(
      statusLabel ? `${statusLabel.value} (${status.value})` : status.value,
      status.id
    )
  sheet.write(cmmsDocumentLabel(document.interpretation), {
    size: 9,
    muted: true,
  })
  for (const section of document.sections) {
    const nodes = section.nodes.filter(
      (node) => node !== titleNode && node !== numberNode && node !== status
    )
    sheet.section(cmmsDocumentLabel(section.label), section.id)
    let fields: { id: string; label: string; value: string }[] = []
    const flush = () => {
      sheet.fields(fields)
      fields = []
    }
    for (const node of nodes) {
      const field = {
        id: node.id,
        label: cmmsDocumentLabel(node.label),
        value: displayValue(node),
      }
      if (node.kind === 'paragraph') {
        flush()
        sheet.write(field.value, { field: field.id })
      } else fields.push(field)
    }
    flush()
  }
  sheet.section(t`Source record`)
  sheet.fields([
    {
      id: 'source.system',
      label: t`Source system`,
      value: entry.data.source.system,
    },
    { id: 'source.site', label: t`Site`, value: entry.data.source.site },
    { id: 'source.key', label: t`Source key`, value: entry.data.source.key },
    {
      id: 'source.version',
      label: t`Source version`,
      value: entry.data.source.version,
    },
  ])
  sheet.footer(entry.data.source)
  // Python evidence offsets count Unicode code points, not JavaScript UTF-16 units.
  const text = Array.from(document.evidence.text)
  return {
    blob: pdf.output('blob'),
    pageCount: pdf.getNumberOfPages(),
    runs: sheet.runs,
    documentRevision: document.revision,
    evidence: document.evidence.spans.map((span) => ({
      id: span.node_id,
      text: text.slice(span.start, span.end).join(''),
    })),
  }
}
